import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ContextProps, TGroup, TOptionValue, TMultiValue } from './types';

export const GroupSelectContext = createContext<ContextProps>({} as ContextProps);

export function useGroupSelectContext() {
  return useContext(GroupSelectContext);
}

type Props<TGroupNames extends string = string> = {
  children?: React.ReactNode;
  groups: TGroup<TGroupNames>[];
  value: Partial<TMultiValue<TGroupNames>>;
  isMulti: boolean;
};

export function GroupSelectProvider<TGroupNames extends string = string>({ children, value, isMulti, groups }: Props<TGroupNames>) {
  const [selectedGroup, setSelectedGroup] = useState<TGroupNames>(groups.find((g) => !g.hidden).name);
  const [values, setValues] = useState<Partial<TMultiValue<TGroupNames>>>(value);
  const [shouldDirty, setShouldDirty] = useState(false);

  useEffect(() => {
    setValues(value);
  }, [value]);

  const handleSelect = useCallback(
    (group: TGroupNames, optionValue: string) => {
      setShouldDirty(true);

      setValues((prevValue): Partial<TMultiValue<TGroupNames>> => {
        const currentGroupValue = prevValue[group] ?? [];
        const hasOption = !!currentGroupValue.find((option) => option === optionValue);

        if (!isMulti) {
          return {
            [group]: hasOption ? [] : [optionValue],
          } as Partial<TMultiValue<TGroupNames>>;
        }

        return {
          ...prevValue,
          [group]: hasOption ? currentGroupValue.filter((option) => option !== optionValue) : [...currentGroupValue, optionValue],
        };
      });
    },
    [isMulti],
  );

  const handleSelectAll = useCallback(
    (group: TGroupNames) => {
      if (!isMulti) return;
      setShouldDirty(true);

      setValues((prevValue) => {
        const groupData = groups.find((g) => g.name === group);
        if (!groupData) return prevValue;

        const isEveryOptionSelected = groupData.options.every((o) => prevValue[group]?.includes(o.value));
        const groupOptions = groupData.options.map((option) => option.value);

        return {
          ...prevValue,
          [group]: isEveryOptionSelected ? [] : groupOptions,
        };
      });
    },
    [isMulti, groups],
  );

  /**
   * If group parameter passed - clear only this group, else clear all groups
   */
  const handleClear = useCallback((clearGroupName?: TGroupNames) => {
    setShouldDirty(true);

    setValues((prevValue) => {
      const hasClearGroup = !!clearGroupName && Object.keys(prevValue).some((groupName) => groupName === clearGroupName);
      return Object.fromEntries(
        Object.entries(prevValue).map(([groupName, groupValue]: [string, TOptionValue[]]) => {
          if (!hasClearGroup) {
            return [groupName, []];
          }

          if (groupName === clearGroupName) {
            return [groupName, []];
          }

          return [groupName, groupValue];
        }),
      ) as Partial<TMultiValue<TGroupNames>>;
    });
  }, []);

  return (
    <GroupSelectContext.Provider
      value={{
        groups,
        values,
        selectedGroup,
        setSelectedGroup: (group: TGroupNames) => setSelectedGroup(group),
        handleSelect,
        handleSelectAll,
        isMulti,
        shouldDirty,
        handleClear,
      }}
    >
      {children}
    </GroupSelectContext.Provider>
  );
}
